import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Users, Plus, Pencil, Trash2, Save, X, Loader2, Eye, EyeOff,
  Upload, ArrowUp, ArrowDown, RefreshCw, Crop,
} from 'lucide-react';
import { API, BACKEND_URL } from '@/config';
import ImageCropperModal from './ImageCropperModal';

const resolveLogoUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

const SCALE_OPTIONS = [
  { value: '', label: 'Κανονικό' },
  { value: 'scale-110', label: '110%' },
  { value: 'scale-125', label: '125%' },
  { value: 'scale-150', label: '150%' },
];

export const PartnersManagementPanel = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/partners-config`);
      setPartners(data.partners || []);
    } catch (e) {
      console.error('Failed to fetch partners', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSave = async (partnerData, partnerId) => {
    try {
      if (partnerId) {
        await axios.patch(`${API}/admin/partners-config/${partnerId}`, partnerData);
      } else {
        await axios.post(`${API}/admin/partners-config`, partnerData);
      }
      await fetchAll();
      setEditing(null);
      setShowAdd(false);
    } catch (e) {
      console.error('Save failed', e);
      alert('Αποτυχία αποθήκευσης');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Διαγραφή "${name}"; Η ενέργεια δεν αναιρείται.`)) return;
    try {
      await axios.delete(`${API}/admin/partners-config/${id}`);
      await fetchAll();
    } catch (e) {
      console.error('Delete failed', e);
      alert('Αποτυχία διαγραφής');
    }
  };

  const handleToggleActive = async (p) => {
    await handleSave({ is_active: !p.is_active }, p.id);
  };

  const handleReorder = async (id, direction) => {
    const idx = partners.findIndex(p => p.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= partners.length) return;
    const a = partners[idx];
    const b = partners[newIdx];
    try {
      await axios.patch(`${API}/admin/partners-config/${a.id}`, { display_order: b.display_order });
      await axios.patch(`${API}/admin/partners-config/${b.id}`, { display_order: a.display_order });
      await fetchAll();
    } catch (e) {
      console.error('Reorder failed', e);
    }
  };

  return (
    <div className="space-y-4" data-testid="partners-management-panel">
      <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-gold" />
            Διαχείριση Συνεργατών
            <span className="ml-2 bg-gold/20 text-gold text-xs font-bold px-2 py-0.5 rounded-full">
              {partners.length}
            </span>
          </h2>
          <div className="flex gap-2">
            <button
              onClick={fetchAll}
              disabled={loading}
              className="px-3 py-1.5 bg-navy-200 text-slate-300 border border-slate-700 rounded-lg hover:bg-navy-200/70 text-sm inline-flex items-center gap-1.5"
              data-testid="partners-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Ανανέωση
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="px-4 py-1.5 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-lg hover:from-amber-500 hover:to-gold text-sm inline-flex items-center gap-1.5"
              data-testid="partners-add-btn"
            >
              <Plus className="w-4 h-4" />
              Προσθήκη Συνεργάτη
            </button>
          </div>
        </div>
        <p className="text-slate-400 text-sm mt-3">
          Διαχειριστείτε τα logos συνεργατών που εμφανίζονται στην αρχική σελίδα. Μπορείτε να ανεβάσετε νέα εικόνα ή να βάλετε URL, να αλλάξετε το zoom κάθε λογότυπου, και να αλλάξετε σειρά.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map((p, idx) => (
            <div
              key={p.id}
              className={`bg-navy-100/50 rounded-xl border p-4 ${p.is_active ? 'border-slate-700/50' : 'border-red-500/20 opacity-60'}`}
              data-testid={`partner-card-${p.id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-20 h-20 bg-white rounded-lg border-2 border-gold/40 overflow-hidden flex items-center justify-center flex-shrink-0">
                  <img
                    src={resolveLogoUrl(p.logo_url)}
                    alt={p.name}
                    className={`w-full h-full object-contain ${p.zoom ? 'scale-150' : ''} ${p.scale || ''}`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white truncate" title={p.name}>{p.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {p.zoom ? '🔍 Zoom 150% ' : ''}{p.scale ? `• ${p.scale}` : ''}
                    {!p.zoom && !p.scale && 'Κανονικό μέγεθος'}
                  </p>
                  <p className="text-xs text-slate-500">Σειρά: #{idx + 1}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-1">
                <button
                  onClick={() => setEditing(p)}
                  className="px-2.5 py-1 bg-gold/15 text-gold border border-gold/30 rounded text-xs hover:bg-gold/25 inline-flex items-center gap-1"
                  data-testid={`partner-edit-btn-${p.id}`}
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button
                  onClick={() => handleToggleActive(p)}
                  className={`px-2.5 py-1 border rounded text-xs inline-flex items-center gap-1 ${
                    p.is_active
                      ? 'bg-amber-500/15 text-amber-400 border-amber-500/30 hover:bg-amber-500/25'
                      : 'bg-green-500/15 text-green-400 border-green-500/30 hover:bg-green-500/25'
                  }`}
                  data-testid={`partner-toggle-btn-${p.id}`}
                >
                  {p.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {p.is_active ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => handleReorder(p.id, -1)}
                  disabled={idx === 0}
                  className="px-2 py-1 bg-navy-200 text-slate-300 border border-slate-700 rounded text-xs hover:bg-navy-200/70 disabled:opacity-30"
                  title="Μετακίνηση πάνω"
                >
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleReorder(p.id, 1)}
                  disabled={idx === partners.length - 1}
                  className="px-2 py-1 bg-navy-200 text-slate-300 border border-slate-700 rounded text-xs hover:bg-navy-200/70 disabled:opacity-30"
                  title="Μετακίνηση κάτω"
                >
                  <ArrowDown className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleDelete(p.id, p.name)}
                  className="ml-auto px-2.5 py-1 bg-red-500/15 text-red-400 border border-red-500/30 rounded text-xs hover:bg-red-500/25 inline-flex items-center gap-1"
                  data-testid={`partner-delete-btn-${p.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(editing || showAdd) && (
        <PartnerEditModal
          partner={editing}
          onClose={() => { setEditing(null); setShowAdd(false); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const PartnerEditModal = ({ partner, onClose, onSave }) => {
  const [name, setName] = useState(partner?.name || '');
  const [logoUrl, setLogoUrl] = useState(partner?.logo_url || '');
  const [zoom, setZoom] = useState(!!partner?.zoom);
  const [scale, setScale] = useState(partner?.scale || '');
  const [isActive, setIsActive] = useState(partner?.is_active !== false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState('');

  const uploadBlob = async (blob, originalName = 'logo.png') => {
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', blob, originalName);
      const { data } = await axios.post(`${API}/admin/partners-config/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(data.logo_url);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || 'Αποτυχία ανεβάσματος');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Open cropper with this file's data URL
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropExisting = async () => {
    if (!logoUrl) return;
    // Convert URL to data URL via fetch (handles CORS-friendly URLs and same-origin uploads)
    setError('');
    try {
      const url = logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}${logoUrl}`;
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      setCropSrc(dataUrl);
      setCropperOpen(true);
    } catch (e) {
      console.error(e);
      setError('Δεν είναι δυνατό το crop αυτής της εικόνας (CORS). Ανεβάστε ξανά την εικόνα.');
    }
  };

  const handleCropComplete = async (blob) => {
    await uploadBlob(blob, 'cropped.png');
  };

  const handleSubmit = async () => {
    if (!name.trim() || !logoUrl.trim()) {
      setError('Όνομα και logo URL απαιτούνται');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        logo_url: logoUrl.trim(),
        zoom,
        scale,
        is_active: isActive,
      }, partner?.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-navy-100 border border-gold/30 rounded-2xl shadow-2xl max-w-xl w-full my-8 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white" data-testid="partner-modal-title">
            {partner ? 'Επεξεργασία Συνεργάτη' : 'Νέος Συνεργάτης'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">Όνομα <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="π.χ. Mediterranean Hospital"
              className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none"
              data-testid="partner-name-input"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Logo URL <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://... ή ανεβάστε αρχείο παρακάτω"
              className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none font-mono text-xs"
              data-testid="partner-logo-url-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              data-testid="partner-logo-file-input"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-3 py-1.5 bg-gold/15 text-gold border border-gold/30 rounded-lg text-sm hover:bg-gold/25 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="partner-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Ανέβασμα...' : 'Ανέβασμα + Crop'}
              </button>
              {logoUrl && (
                <button
                  type="button"
                  onClick={handleCropExisting}
                  disabled={uploading}
                  className="px-3 py-1.5 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-lg text-sm hover:bg-blue-500/25 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="partner-crop-existing-btn"
                >
                  <Crop className="w-4 h-4" />
                  Crop Υπάρχουσας
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">JPG/PNG/WEBP/SVG • Max 5MB • Crop σε τετράγωνο</p>
          </div>

          {logoUrl && (
            <div>
              <label className="block text-sm text-slate-300 mb-1">Προεπισκόπηση</label>
              <div className="w-32 h-32 bg-white rounded-lg border-2 border-gold/40 overflow-hidden flex items-center justify-center p-1">
                <img
                  src={resolveLogoUrl(logoUrl)}
                  alt="Preview"
                  className={`w-full h-full object-contain ${zoom ? 'scale-150' : ''} ${scale}`}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Zoom (150%)</label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={zoom}
                  onChange={(e) => setZoom(e.target.checked)}
                  className="accent-gold w-4 h-4"
                  data-testid="partner-zoom-checkbox"
                />
                <span className="text-sm text-slate-300">Μεγαλύτερο zoom</span>
              </label>
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Επιπλέον scale</label>
              <select
                value={scale}
                onChange={(e) => setScale(e.target.value)}
                className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none text-sm"
                data-testid="partner-scale-select"
              >
                {SCALE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="accent-gold w-4 h-4"
                data-testid="partner-active-checkbox"
              />
              <span className="text-sm text-slate-300">Ενεργός (φαίνεται στην αρχική σελίδα)</span>
            </label>
          </div>

          {error && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-800 flex gap-3 justify-end">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-slate-300 hover:text-white">Ακύρωση</button>
          <button
            onClick={handleSubmit}
            disabled={saving || uploading}
            className="px-5 py-2 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-lg hover:from-amber-500 hover:to-gold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="partner-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Αποθήκευση
          </button>
        </div>
      </div>

      <ImageCropperModal
        isOpen={cropperOpen}
        onClose={() => setCropperOpen(false)}
        imageSrc={cropSrc}
        aspect={1}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
};

export default PartnersManagementPanel;
